import { LocalTimeRange } from '@ftr/contracts/type/shared'
import { AccessLevelEnum } from '@ftr/distribution-contracts'
import { LocalDate, LocalTime, ZoneId } from '@js-joda/core'
import z from 'zod'
import { ONGOING_END_TIME_PROXY } from '../../utils'

const IdNamePairSchema = z.object({
  id: z.uuid(),
  name: z.string(),
})
export type IdNamePair = z.infer<typeof IdNamePairSchema>

const CartCourtSystemSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    timezoneId: z.string(),
  })
  .transform(serialized => ({
    ...serialized,
    timezoneId: ZoneId.of(serialized.timezoneId),
  }))
export type SerializedCartCourtSystem = z.input<typeof CartCourtSystemSchema>
export type CartCourtSystem = z.output<typeof CartCourtSystemSchema>

const CartTimeSchema = z
  .object({
    start: z.string(),
    end: z.string().nullable(),
  })
  .transform(
    ({ start, end }) => new LocalTimeRange(LocalTime.parse(start), end ? LocalTime.parse(end) : ONGOING_END_TIME_PROXY),
  )
export type SerializedCartTime = z.input<typeof CartTimeSchema>
export type CartTime = z.output<typeof CartTimeSchema>

const CartItemRuleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  accessLevel: AccessLevelEnum,
  publicStatement: z.string(),
})
export type CartItemRule = z.infer<typeof CartItemRuleSchema>

const CartItemSchema = z
  .object({
    id: z.uuid(),
    recordingId: z.uuid(),
    contentSectionId: z.string(),
    departmentId: z.uuid().optional(),
    courthouse: IdNamePairSchema,
    courtroom: IdNamePairSchema,
    recordingDate: z.string(),
    rule: CartItemRuleSchema,
    contentSectionTime: CartTimeSchema,
    requestedTimes: CartTimeSchema.array(),
    sealed: z.boolean(),
  })
  .transform(serialized => ({
    ...serialized,
    recordingDate: LocalDate.parse(serialized.recordingDate),
  }))
export type SerializedCartItem = z.input<typeof CartItemSchema>
export type CartItem = z.output<typeof CartItemSchema>

export const CartStateModelSchema = z.object({
  courtSystem: CartCourtSystemSchema.optional(),
  cartItems: CartItemSchema.array(),
})
export type SerializedCartStateModel = z.input<typeof CartStateModelSchema>
export type CartStateModel = z.output<typeof CartStateModelSchema>

export interface CartGroup {
  key: string
  courthouse: IdNamePair
  courtroom: IdNamePair
  recordingDate: LocalDate
  items: CartItem[]
}

export const CART_STATE_DEFAULT: CartStateModel = { courtSystem: undefined, cartItems: [] }
